import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { accountsEnabled, request } from './AdultAuthPanel';
import GamePage from '../pages/GamePage';
import StudentProgressGate from './StudentProgressGate';
import StudentPinPad from './StudentPinPad';
import './StudentAccess.css';
import { SpaceIllustration, MissionSteps } from './AccountsVisuals';

// Child sessions never use the adult refresh helper.
let refreshFlight = null;
export async function requestWithStudentRefresh(path, options = {}, requestFn = request) {
  try { return await requestFn(path, options); }
  catch (error) { if (error?.status !== 401) throw error; }
  if (!refreshFlight) {
    refreshFlight = requestFn('/student-auth/refresh', { method: 'POST' }).finally(() => { refreshFlight = null; });
  }
  await refreshFlight;
  return requestFn(path, options);
}

export default function StudentAccessPage() {
  const navigate = useNavigate();
  const [codes, setCodes] = useState(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!accountsEnabled) return <Navigate to="/" replace />;
  async function login(event) {
    event.preventDefault(); setBusy(true); setError('');
    try {
      await request('/student-auth/login', { method: 'POST', body: JSON.stringify({ ...codes, pin }) });
      setPin(''); navigate('/student/play', { replace: true });
    } catch (failure) {
      setPin('');
      setError(failure.status === 429 ? 'Espera unos minutos antes de volver a intentar. Pide ayuda a un adulto.' : 'No pudimos entrar. Revisa tu código y PIN con tu maestro.');
    } finally { setBusy(false); }
  }
  return <main className="tmag-child-page"><section className="tmag-child-panel">
    <header className="tmag-child-welcome"><SpaceIllustration compact /><span className="tmag-child-eyebrow">MISIÓN ESCUELA · TECHNOMATHPR</span>
    <h1>{codes ? 'Ahora escribe tu PIN' : 'Escribe tu código de acceso'}</h1></header>
    <MissionSteps steps={['Código', 'PIN', '¡Jugar!']} current={codes ? 1 : 0} />
    {error && <p role="alert">{error}</p>}
    {!codes ? <form onSubmit={(event) => { event.preventDefault(); const data = new FormData(event.currentTarget); setCodes({ access_code: data.get('access_code').trim() }); }}>
      <p>Busca el código en la tarjeta que te dio tu maestro.</p>
      <label>Código de acceso<input name="access_code" required maxLength="32" placeholder="Ej. K7M4QP2A" className="tmag-child-access-code" autoComplete="off" autoCapitalize="characters" spellCheck="false" /></label>
      <button className="tmag-child-primary">Continuar →</button>
    </form> : <form onSubmit={login}>
      <StudentPinPad value={pin} onChange={setPin} disabled={busy} />
      <button className="tmag-child-primary" disabled={busy || pin.length < 4}>{busy ? 'Entrando…' : '¡Jugar!'}</button>
      <button type="button" disabled={busy} onClick={() => { setCodes(null); setPin(''); setError(''); }}>Cambiar código</button>
    </form>}
    <p className="tmag-child-help">¿Juegas en casa? Pide a un adulto que elija tu perfil en Familia.</p>
    <nav><Link to="/account">Entrada de adultos</Link><Link to="/">Volver a jugar gratis</Link></nav>
  </section></main>;
}

export function StudentPlayPage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!accountsEnabled) return;
    let active = true;
    async function check() {
      try {
        const next = await requestWithStudentRefresh('/student-auth/me');
        if (active) { setProfile(next); setError(''); }
      } catch (failure) {
        if (!active) return;
        if (failure.status === 401) { setProfile(null); navigate('/student', { replace: true }); }
        else setError('No pudimos comprobar tu perfil. Vuelve a intentar.');
      }
    }
    check();
    // Recheck authority during play, on focus, and before the access cookie expires.
    const timer = window.setInterval(check, 60000);
    window.addEventListener('focus', check);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener('focus', check); };
  }, [navigate, revision]);
  if (!accountsEnabled) return <Navigate to="/" replace />;
  async function exit() {
    setBusy(true); setError('');
    try {
      await request('/student-auth/logout', { method: 'POST' });
      setProfile(null); navigate('/account', { replace: true });
      return true;
    } catch { setError('No pudimos salir. Reintenta para cerrar tu perfil de forma segura.'); return false; }
    finally { setBusy(false); }
  }
  return <div className="tmag-child-play">
    {error && <p className="tmag-child-notice" role="alert">{error} <button onClick={() => setRevision((v) => v + 1)}>Reintentar</button></p>}
    {profile ? <StudentProgressGate key={profile.student_id} student={profile} requestFn={requestWithStudentRefresh} onExit={exit}>{safeExit => <GamePage student={profile} onStudentExit={safeExit} exitBusy={busy} />}</StudentProgressGate> : <main className="tmag-child-page"><section className="tmag-child-panel"><p role="status">Preparando tu misión…</p><Link to="/">Volver a jugar gratis</Link></section></main>}
  </div>;
}
